// M8 已切真后端（/api/comments）
// 留言管理:状态筛选(全部/精选/未精选/待回复/已回复) + 搜索 + 表格 + 批量精选
// 对齐 WeChat MP 「留言管理」:Tag 用 WeChat 状态色,操作改 antd type="link" 用 #576B95 微信蓝
import { useState } from 'react'
import { Input, Segmented, Tag, Button, Space, Avatar, App as AntdApp } from 'antd'
import { Search, Star, MessageCircle, CheckSquare } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import DataTable from '@/components/DataTable'
import type { ColumnsType } from 'antd/es/table'
import { useCommentList, type CommentItem, type CommentStatus } from '@/mocks/comments'
import { timeAgo } from '@/lib/utils'

type FilterKey = 'all' | 'elected' | 'unelected' | 'pending' | 'replied'

export default function Comments() {
  const { message } = AntdApp.useApp()
  const [filter, setFilter] = useState<FilterKey>('all')
  const [search, setSearch] = useState('')
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([])

  // 内部 API 状态只有 elected/normal/pending
  const apiStatus: CommentStatus | undefined =
    filter === 'all' ? undefined
    : filter === 'elected' ? 'elected'
    : filter === 'pending' ? 'pending'
    : 'normal'

  const { data, isLoading } = useCommentList({ status: apiStatus, search })

  const columns: ColumnsType<CommentItem> = [
    {
      title: '用户',
      key: 'user',
      width: 150,
      render: (_v, r) => (
        <div className="flex items-center gap-2">
          <Avatar size={24} className="!bg-primary-100 !text-primary-700 flex-shrink-0 !text-xs">
            {r.nickname?.[0] ?? '?'}
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm text-ink-900 truncate">{r.nickname ?? '(匿名)'}</div>
            <div className="text-xs text-ink-300">留言 {r.commentCount} 次</div>
          </div>
        </div>
      ),
    },
    {
      title: '文章',
      dataIndex: 'articleTitle',
      key: 'articleTitle',
      width: 200,
      responsive: ['md'],
      ellipsis: true,
    },
    {
      title: '留言',
      dataIndex: 'content',
      key: 'content',
      render: (v: string) => (
        <span className="text-ink-700 line-clamp-2" title={v}>
          {v}
        </span>
      ),
    },
    {
      title: '时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 110,
      render: (v: number) => (
        <span className="text-ink-500 text-sm num" title={new Date(v).toLocaleString('zh-CN')}>
          {timeAgo(v)}
        </span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v: CommentStatus) => {
        if (v === 'elected') return <Tag color="success">精选</Tag>
        if (v === 'pending') return <Tag color="warning">待回复</Tag>
        return <Tag>未精选</Tag>
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 160,
      render: (_v, r) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            style={{ color: '#576B95', padding: 0 }}
            icon={<Star size={12} />}
            disabled={r.status === 'elected'}
            onClick={() => {
              // v1 mock — 待 M9 接 POST /api/comments/{id}/elect
              message.info(`已请求精选留言 #${r.id}(M9 接入)`)
            }}
          >
            精选
          </Button>
          <Button
            type="link"
            size="small"
            style={{ color: '#576B95', padding: 0 }}
            icon={<MessageCircle size={12} />}
            onClick={() => {
              // v1 mock
              message.info(`已打开回复面板 #${r.id}(M9 接入)`)
            }}
          >
            回复
          </Button>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="留言管理"
        subtitle="所有留言的统一管理入口"
        extra={
          <Space size="middle">
            {selectedKeys.length > 0 && (
              <Button
                type="primary"
                size="small"
                icon={<CheckSquare size={13} />}
                onClick={() => {
                  message.success(`已请求批量精选 ${selectedKeys.length} 条(M9 接入)`)
                  setSelectedKeys([])
                }}
              >
                批量精选 ({selectedKeys.length})
              </Button>
            )}
            <span className="text-sm text-ink-500 num">
              共 {data?.length ?? 0} 条
            </span>
          </Space>
        }
      />

      {/* 工具栏 */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Segmented
          value={filter}
          onChange={(v) => {
            setFilter(v as FilterKey)
            setSelectedKeys([])
          }}
          options={[
            { label: '全部', value: 'all' },
            { label: '精选', value: 'elected' },
            { label: '未精选', value: 'unelected' },
            { label: '待回复', value: 'pending' },
            { label: '已回复', value: 'replied' },
          ]}
        />
        <Input
          allowClear
          prefix={<Search size={14} className="text-ink-300" />}
          placeholder="搜索用户 / 留言内容"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="!w-72"
        />
      </div>

      <DataTable
        columns={columns}
        dataSource={data ?? []}
        loading={isLoading}
        rowKey="id"
        size="small"
        scroll={{ x: 900 }}
        pagination={{ pageSize: 15, showSizeChanger: false, showTotal: (t: number) => `共 ${t} 条` }}
      />
    </div>
  )
}
